import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useGetEventChecklist,
  useListUsers,
  useListTaskTemplates,
  useStartProcedure,
  getListUsersQueryKey,
  getGetEventChecklistQueryKey,
} from "@workspace/api-client-react";
import type { EventChecklistEntry, EventChecklistInstance, TaskTemplateDetail, User } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MemberCombobox } from "@/components/operations/member-combobox";
import { ListChecks, ExternalLink, PlayCircle } from "lucide-react";
import { groupChecklistItems, ChecklistItemGroup } from "./checklist-daily-tab";

// Phase 49 Plan 08 (TEVT/TASN/TCHN) — the Onboarding/Exit tabs of the
// tabbed Checklist page. Unlike Daily/Weekly/Monthly (a 1:1 template<->task
// feed, 43-05), an event template can have N concurrently open instances
// (e.g. 3 Day Passes checking in at once) — 49-RESEARCH Pattern 11 required
// a NEW read shape (`GET /tasks/checklist/events`) instead of reusing
// DailyChecklistEntry. Grouping = ONE card per procedure TYPE (template),
// each holding a nested list of instance sub-cards (one per open task),
// exactly per 49-CONTEXT ("Grouping within a tab = by procedure type").
//
// Reuses the SAME ChecklistItemRow two-step (done/N/A/validate + person-
// vs-role avatar) as the period tabs — one render path for every checklist
// item in the app, period or event (checklist-daily-tab.tsx exports it).
//
// Consumes ONLY generated hooks: useGetEventChecklist (49-03/49-07's
// N-per-template read feed) + useListUsers (same admin-gated roster +
// usersUnavailable graceful degradation as the period tabs).

/** No dedicated "is this the refund-handoff item" flag exists on the
 * checklist item schema (by design — Settings ▸ Checklists authors a plain
 * label, 49-CONTEXT/49-RESEARCH Pattern 9: "a checklist item that deep-links
 * to the existing Phase-36.1 refund flow", not a new mechanism). The exit
 * templates' own DB-integration test fixtures (49-06/49-11) consistently
 * label this item something containing "refund" (e.g. "Process deposit
 * refund — hand off to the account's Security Deposits tab"). This
 * case-insensitive substring match is the pragmatic, display-only heuristic
 * for attaching the "View account" deep link — it never gates the item's
 * done/N/A/validate actionability, only whether the nav link renders. */
const REFUND_ITEM_LABEL_RE = /refund/i;

function bookingTypeLabel(bookingType: string | null): string {
  if (!bookingType) return "General procedure";
  return bookingType
    .split("_")
    .map((word) => (word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

const TRIGGER_META: Record<"onboarding" | "exit", { subtitle: string; empty: string }> = {
  onboarding: {
    subtitle: "Move-in procedures spawned when a booking starts or a seat is assigned — one card per booking type, one sub-card per checking-in account.",
    empty: "No onboarding procedures open right now — they spawn automatically when a booking starts or a seat is assigned.",
  },
  exit: {
    subtitle: "Move-out procedures spawned when a seat is released or a membership exits — including the deposit-refund handoff (this tab never moves money).",
    empty: "No exit procedures open right now — they spawn automatically when a seat is released or a membership exits.",
  },
};

function EventInstanceCard({ instance, isExit, users, usersUnavailable }: {
  instance: EventChecklistInstance;
  isExit: boolean;
  users: User[] | undefined;
  usersUnavailable: boolean;
}) {
  // Group-checklist UI (migration 0120) — same collapsible-section rendering
  // as the Daily/Weekly/Monthly tab (checklist-daily-tab.tsx), reused
  // verbatim so every checklist surface in the app groups items identically.
  const groups = groupChecklistItems(instance.items);
  return (
    <div className="rounded-md border p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {instance.accountName ?? "Manually started (no account context)"}
        </span>
        <Badge variant="secondary" className="shrink-0 whitespace-nowrap text-[10px]">Task #{instance.taskId}</Badge>
      </div>
      <div className="space-y-2">
        {groups.map((g) => (
          <ChecklistItemGroup
            key={g.key}
            group={g}
            users={users}
            usersUnavailable={usersUnavailable}
            accountDeepLinkFor={(item) =>
              isExit && instance.accountId != null && REFUND_ITEM_LABEL_RE.test(item.label)
                ? { accountId: instance.accountId }
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

function EventTemplateCard({ entry, isExit, users, usersUnavailable }: {
  entry: EventChecklistEntry;
  isExit: boolean;
  users: User[] | undefined;
  usersUnavailable: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{entry.templateName}</CardTitle>
            <CardDescription>{bookingTypeLabel(entry.eventBookingType)}</CardDescription>
          </div>
          <Badge variant="secondary" className="whitespace-nowrap">
            {entry.instances.length} open
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {entry.instances.map((instance) => (
          <EventInstanceCard
            key={instance.taskId}
            instance={instance}
            isExit={isExit}
            users={users}
            usersUnavailable={usersUnavailable}
          />
        ))}
      </CardContent>
    </Card>
  );
}

// Phase 49 verification gap-fix (TEVT-06, 2026-07-15) — the ONLY spawn path
// for Team Cabin / Visitor (no backing booking schema this phase, so they
// never reach the auto-trigger seat-assign/scheduler hooks, per 49-RESEARCH).
// POST /tasks/checklist/start-procedure was fully built + tested but had
// zero call sites anywhere in the CRM — this dialog is that missing
// affordance. Consumes ONLY the generated useStartProcedure mutation +
// useListTaskTemplates (to populate the template picker); templateId is the
// only field StartProcedureBody actually requires (accountId/eventSourceType/
// eventSourceId are all optional — "Omit for a context-free manual start",
// per the openapi field description), so this dialog supplies just those two.
const MANUAL_ONBOARDING_BOOKING_TYPES = ["team_cabin", "visitor"] as const;

function errMessage(err: unknown, fallback: string): string {
  const data = (err as { data?: Record<string, unknown> })?.data;
  return (data?.message as string) ?? (data?.error as string) ?? fallback;
}

function StartProcedureDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const { data: templates } = useListTaskTemplates();
  const startProcedure = useStartProcedure();

  const [templateId, setTemplateId] = useState<string>("");
  const [accountId, setAccountId] = useState<number | null>(null);

  // Team Cabin / Visitor only — the manual-trigger taxonomy locked in
  // 49-CONTEXT (the 5 booking types WITH real backing schema auto-trigger
  // instead; the route itself is generic over any active onboarding/exit
  // template, but this picker is scoped to the templates that actually NEED
  // this button per the operator's own taxonomy).
  const manualTemplates: TaskTemplateDetail[] = (templates ?? []).filter(
    (t) => t.active && t.trigger === "onboarding" && t.eventBookingType != null && (MANUAL_ONBOARDING_BOOKING_TYPES as readonly string[]).includes(t.eventBookingType),
  );

  function reset() {
    setTemplateId("");
    setAccountId(null);
  }

  async function handleSubmit() {
    if (!templateId || startProcedure.isPending) return;
    try {
      await startProcedure.mutateAsync({ data: { templateId: Number(templateId), accountId: accountId ?? undefined } });
      toast.success("Procedure started — check the Onboarding tab below.");
      // Broad prefix invalidation (no params -> no `exact`) — matches BOTH
      // the onboarding- and exit-scoped query keys
      // (getGetEventChecklistQueryKey({trigger})) so whichever tab is open
      // re-fetches immediately.
      queryClient.invalidateQueries({ queryKey: getGetEventChecklistQueryKey() });
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(errMessage(err, "Could not start this procedure."));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Start procedure</DialogTitle>
          <DialogDescription>
            Manually spawn a Team Cabin or Visitor onboarding checklist — their only spawn path (no auto-trigger this phase).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="start-procedure-template">Procedure</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger id="start-procedure-template">
                <SelectValue placeholder="Select a Team Cabin / Visitor procedure…" />
              </SelectTrigger>
              <SelectContent>
                {manualTemplates.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    No active Team Cabin / Visitor template configured yet — author one in Settings ▸ Checklists.
                  </div>
                ) : (
                  manualTemplates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Account (optional)</Label>
              {accountId != null && (
                <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setAccountId(null)}>
                  Clear
                </Button>
              )}
            </div>
            <MemberCombobox value={accountId} onChange={setAccountId} className="h-10" />
            <p className="text-xs text-muted-foreground">
              Leave blank for a context-free start (e.g. a Visitor with no booking row).
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} disabled={!templateId || startProcedure.isPending}>
            {startProcedure.isPending ? "Starting…" : "Start procedure"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChecklistEventTab({ trigger }: { trigger: "onboarding" | "exit" }) {
  const { user } = useAuth();
  const { data: entries, isLoading } = useGetEventChecklist({ trigger });
  // Mirrors checklist-daily-tab.tsx: retry:false so a 403 (non-admin viewer)
  // settles into isError quickly instead of retrying a request that will
  // never succeed for this role.
  const { data: users, isError: usersUnavailable } = useListUsers({
    query: { retry: false, queryKey: getListUsersQueryKey() },
  });
  const [startOpen, setStartOpen] = useState(false);

  const list = entries ?? [];
  const isExit = trigger === "exit";
  const meta = TRIGGER_META[trigger];
  // Mirrors POST /tasks/checklist/start-procedure's own canEditConfig gate
  // (admin/super_admin/ops_lead) — hiding the button for anyone the server
  // would 403 anyway (same pattern as checklists-settings.tsx's `canEdit`).
  const canStartProcedure = user?.role === "admin" || user?.role === "super_admin" || user?.role === "ops_lead";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm text-muted-foreground">{meta.subtitle}</p>
        {/* TEVT-06 — Team Cabin / Visitor have NO auto-trigger this phase;
            this button is their only spawn path. Onboarding-only (per
            49-CONTEXT's manual-trigger taxonomy) — the Exit tab never shows it. */}
        {!isExit && canStartProcedure && (
          <Button type="button" variant="outline" className="w-full shrink-0 gap-2 sm:w-auto" onClick={() => setStartOpen(true)}>
            <PlayCircle className="h-4 w-4" />
            Start procedure
          </Button>
        )}
      </div>

      {!isExit && <StartProcedureDialog open={startOpen} onOpenChange={setStartOpen} />}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-3"><Skeleton className="h-5 w-40" /><Skeleton className="mt-2 h-4 w-32" /></CardHeader>
              <CardContent className="space-y-2">
                {[...Array(2)].map((__, j) => <Skeleton key={j} className="h-16 w-full" />)}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="flex h-40 flex-col items-center justify-center gap-2 text-center">
            <ListChecks className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{meta.empty}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {list.map((entry) => (
            <EventTemplateCard
              key={entry.templateId}
              entry={entry}
              isExit={isExit}
              users={users}
              usersUnavailable={usersUnavailable}
            />
          ))}
        </div>
      )}
    </div>
  );
}
